import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import {
  collection, onSnapshot, getDocs, writeBatch, arrayUnion, serverTimestamp,
  query, where, orderBy, limit, doc, setDoc,
} from 'firebase/firestore';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getActiveRoleFromStorage } from '../utils/getActiveRole';
import { getViewerTimezone } from '../utils/dateTime';
import AskRiaInputBar from '../components/common/AskRiaInputBar';
import { contactService } from '../services/contact.service';
import riaLogo from '../assets/dashboard-logo.png';
import './SpConversationsPage.css';

// ── Constants ─────────────────────────────────────────────────────────────────
const RIA_ID = '__ria_ai__';
const FAVORITES_KEY = '@rialty/sp_pinned_conversations';

const RIA_ENTRY = {
  id: RIA_ID,
  name: 'Ria',
  initials: 'R',
  avatarColor: '#3d2d8f',
  lastMessage: 'Your Real Estate Intelligent Assistant',
  time: '',
  unread: 0,
  isGroupChat: false,
  participants: [],
};

const GROUP_CHAT_COLORS = ['#E91E63','#9C27B0','#3F51B5','#009688','#FF5722','#795548','#607D8B','#2196F3'];
const getGroupColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return GROUP_CHAT_COLORS[Math.abs(h) % GROUP_CHAT_COLORS.length];
};

// ── 2D SVG icons ──────────────────────────────────────────────────────────────
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const StarEmptyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const StarFilledIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const ClipboardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="1" ry="1"/>
    <path d="m9 12 2 2 4-4"/>
  </svg>
);

const UsersIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const RiaBotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="8" width="18" height="12" rx="3"/>
    <path d="M9 8V6a3 3 0 0 1 6 0v2"/>
    <circle cx="9" cy="14" r="1" fill="currentColor" stroke="none"/>
    <circle cx="15" cy="14" r="1" fill="currentColor" stroke="none"/>
  </svg>
);

// ── Icons for the static design preview — mirror /conversations exactly ──────
const ContactPlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/>
    <line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const FilterLinesIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="9" y1="18" x2="15" y2="18"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const FilterAllIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    <line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="12" x2="13" y2="12"/>
  </svg>
);

const FilterUnreadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 4H6a3 3 0 0 0-3 3v11a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3v-8"/>
    <rect x="14" y="2" width="7" height="7" rx="1.5" fill="currentColor" stroke="none"/>
  </svg>
);

const FilterPinnedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22"/>
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
  </svg>
);

const GroupIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const getAvatarColor = (name) => {
  const colors = ['#6c5ce7','#00b894','#fd79a8','#fdcb6e','#74b9ff','#a29bfe','#ff7675','#55efc4'];
  if (!name) return colors[0];
  const sum = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return colors[sum % colors.length];
};

const formatTimestamp = (date) => {
  if (!date) return '';
  const now = new Date();
  const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
  // Clamp to 0 — a message's server timestamp can land a few hundred ms ahead
  // of the local clock (server/client skew), which without clamping makes
  // Math.floor() of the small negative diff come out as -1 instead of 0,
  // rendering "-1d ago" on a message sent seconds ago.
  const diff = Math.max(0, Math.floor((now - d) / 86400000));
  const zone = getViewerTimezone();
  if (diff === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: zone });
};

// Bucketed in the viewer's own profile timezone, not the device's.
const getDateLabel = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const zone = getViewerTimezone();
  const dayKey = (x) => x.toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = dayKey(new Date());
  const dKey = dayKey(d);
  if (dKey === todayKey) return 'Today';
  if (dayKey(new Date(Date.now() - 86400000)) === dKey) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: zone });
};

// ── COMING SOON FLAG — set to false to enable the full conversations UI ───────
// While true, a static design preview renders in place of the coming-soon
// placeholder. Deliberately styled to match /conversations (ConversationPage.js
// + ConversationPage.css) exactly — same colors, avatar/list/header/message/
// composer conventions — so the two pages read as one product, just scoped
// to SP-relevant conversations. Firestore/API wiring below is now live.
const COMING_SOON = false;

// Hides the "Ria" row in the static preview's conversation list without
// removing its markup/data — set back to false to bring it back.
const HIDE_STATIC_RIA_ROW = true;

// ── Static design preview data ────────────────────────────────────────────────
const STATIC_ROWS = [
  { id: 'ria', kind: 'ria', name: 'Ria', preview: 'Your Real Estate Intelligent Assistant' },
  {
    id: 'job-142-maple', kind: 'group', name: '142 Maple Grove Dr', time: '5/27/26',
    unread: 1, groupColor: '#5a7fbf', preview: 'Sarah: Report uploaded, please confirm',
  },
  {
    id: 'dm-sarah', kind: 'dm', name: 'Sarah Nguyen', initials: 'SN', avatarColor: '#5a7fbf',
    time: '5/26/26', starred: true, preview: 'Can you confirm for Jun 3rd?',
  },
  {
    id: 'job-88-ridgeway', kind: 'group', name: '88 Ridgeway Blvd', time: '5/26/26',
    groupColor: '#7a4fa0', preview: 'Rhonda: Hey, confirmed for tomorrow',
  },
  {
    id: 'dm-ethan', kind: 'dm', name: 'Ethan Carter', initials: 'EC', avatarColor: '#8a4a3a',
    time: '5/18/26', preview: "It's done",
  },
  {
    id: 'dm-rhonda', kind: 'dm', name: 'Rhonda James', initials: 'RJ', avatarColor: '#7a4fa0',
    time: '5/15/26', preview: 'Thanks for the quick turnaround!',
  },
  {
    id: 'dm-mike', kind: 'dm', name: 'Mike Kowalski', initials: 'MK', avatarColor: '#4a6a3a',
    time: '5/14/26', preview: 'On my way to the property now',
  },
];

const STATIC_CHAT_ID = 'job-142-maple';

const STATIC_CHAT = {
  name: '142 Maple Grove Dr',
  isGroup: true,
  groupColor: '#5a7fbf',
  participantCount: 3,
  groups: [
    {
      date: 'Yesterday',
      messages: [
        { from: 'them', name: 'Sarah Nguyen', text: 'Hi Jordan, just checking in — are you confirmed for the inspection at 142 Maple Grove on June 3rd?', time: '10:14 AM' },
        { from: 'me', text: "Yes, confirmed! I'll be there at 9 AM sharp. Should take about 2 hours.", time: '10:22 AM' },
        { from: 'them', name: 'Sarah Nguyen', text: "Perfect. The lockbox code is 4821. Seller won't be present.", time: '10:25 AM' },
      ],
    },
    {
      date: 'Today',
      messages: [
        { from: 'them', name: 'Sarah Nguyen', text: "Jordan, I've uploaded the access instructions in the job docs. Please review before tomorrow.", time: '9:41 AM' },
        { from: 'me', text: 'Got it, reviewing now. Will confirm once done.', time: '9:55 AM' },
      ],
    },
  ],
};

// ── Component ─────────────────────────────────────────────────────────────────
const SpConversationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Static design preview state (matches /conversations UI — no data integration)
  const [staticSelectedId, setStaticSelectedId] = useState(STATIC_CHAT_ID);
  const [staticChatOpen, setStaticChatOpen]     = useState(true);
  const [staticSearch, setStaticSearch]         = useState('');
  const [staticFilter, setStaticFilter]         = useState('all');
  const [staticFilterOpen, setStaticFilterOpen] = useState(false);
  const [staticMessageInput, setStaticMessageInput] = useState('');
  const staticFilterRef = useRef(null);

  const handleStaticRowClick = (row) => {
    setStaticSelectedId(row.id);
    setStaticChatOpen(true);
  };

  useEffect(() => {
    if (!staticFilterOpen) return;
    const onDocClick = (e) => {
      if (staticFilterRef.current && !staticFilterRef.current.contains(e.target)) setStaticFilterOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [staticFilterOpen]);

  const staticFilteredRows = STATIC_ROWS.filter(row => {
    if (staticSearch.trim()) return row.name.toLowerCase().includes(staticSearch.trim().toLowerCase());
    if (staticFilter === 'unread') return !!row.unread;
    if (staticFilter === 'pinned') return !!row.starred;
    return true;
  });

  const [conversationList, setConversationList] = useState([]);
  const [selectedId, setSelectedId]             = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages]                 = useState([]);
  const [messageInput, setMessageInput]         = useState('');
  const [loading, setLoading]                   = useState(true);
  const [messagesLoading, setMessagesLoading]   = useState(false);
  const [riaMessages, setRiaMessages]           = useState([]);
  const [searchQuery, setSearchQuery]           = useState('');
  const [activeFilter, setActiveFilter]         = useState('all');
  const [showFilterMenu, setShowFilterMenu]     = useState(false);
  const [favorites, setFavorites]               = useState(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  // "Broadcast to Vendors" — Company-vendorType SP only (see role/vendorType
  // guard below). Not in localStorage's 'user' object, so fetched the same
  // way the "Invite Inspector" dashboard button does.
  const [spVendorType, setSpVendorType]         = useState(null);
  const [showNewChat, setShowNewChat]           = useState(false);
  const [isBroadcasting, setIsBroadcasting]     = useState(false);

  // ── New Chat / Contacts panel state (all SPs, not just COMPANY) ──────────
  // Contact list drives the picker inside the New Chat panel. Fetched via
  // contactService.getContacts(viewingRole='SERVICE_PROVIDER'), which returns
  // every contact of the SP regardless of role.
  const [contacts, setContacts]                 = useState([]);
  const [contactsLoading, setContactsLoading]   = useState(false);
  const [contactsError, setContactsError]       = useState(null);
  const [contactSearch, setContactSearch]       = useState('');
  const [startingChatId, setStartingChatId]     = useState(null);
  // Inline "Add Contact by Email" modal — mirrors the main app's
  // NewChatScreen add-by-email affordance. Server contract: 404 = "no user
  // has that email", 409 = "already a contact".
  const [showAddContact, setShowAddContact]     = useState(false);
  const [addContactEmail, setAddContactEmail]   = useState('');
  const [addContactSubmitting, setAddContactSubmitting] = useState(false);
  const [addContactError, setAddContactError]   = useState(null);

  const messagesAreaRef     = useRef(null);
  const userCacheRef        = useRef({});
  const pendingProfileReqs  = useRef({});
  const messagesListenerRef = useRef(null);
  const decryptedCacheRef   = useRef(new Map());
  const riaConvIdRef        = useRef(null);
  const filterMenuRef       = useRef(null);
  const unreadListenersRef  = useRef({});
  const unreadCountsRef     = useRef({});

  const isRia = selectedId === RIA_ID;

  // Role guard
  useEffect(() => {
    const role = getActiveRoleFromStorage();
    if (role && role.toUpperCase() !== 'SERVICE_PROVIDER') {
      navigate('/conversations', { replace: true });
    }
  }, [navigate]);

  // Vendor type — gates the "Broadcast to Vendors" pencil/panel to Company SPs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) return;
        const resp = await apiClient.get(API_ENDPOINTS.spProfile, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSpVendorType(resp.data?.vendorType || null);
      } catch (e) {
        // Non-critical — pencil button just stays hidden if this fails.
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from Job Detail Chat tab: /conversations/sp?userId=<uid>&userName=<name>
  // — seed the user-name cache so the display name renders correctly even
  // when the backend user-profile lookup returns 404/"Unknown" in this
  // environment, then auto-select the existing 1-on-1 conversation with
  // that user or create one if it doesn't exist yet.
  const pendingUserRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uid = params.get('userId');
    const name = params.get('userName');
    if (!uid) return;
    // Seed the user cache immediately so any resolve done between now and the
    // select uses this name instead of falling through to "Unknown".
    if (name) {
      userCacheRef.current[uid] = {
        name,
        email: '',
        profilePhotoUrl: null,
        ...(userCacheRef.current[uid] || {}),
      };
    }
    pendingUserRef.current = { uid, name };
    // Strip params so a manual refresh doesn't re-trigger.
    navigate('/conversations/sp', { replace: true });
  }, [location.search, navigate]);

  // Once the conversation list loads, resolve the deep-link intent: select
  // the existing 1-on-1 with that user, or POST-create a new chat and wait
  // for it to appear in the list before selecting.
  useEffect(() => {
    const pending = pendingUserRef.current;
    if (!pending || loading) return;
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    const existing = conversationList.find(c =>
      !c.isGroupChat
      && Array.isArray(c.participants)
      && c.participants.includes(pending.uid)
      && c.participants.includes(currentUid)
      && c.participants.length === 2
    );
    if (existing) {
      // Also patch the resolved name on the sidebar entry in case
      // resolveChatDisplayInfo already ran with a stale/Unknown name.
      if (pending.name && existing.name !== pending.name) {
        setConversationList(prev => prev.map(c =>
          c.id === existing.id ? { ...c, name: pending.name, initials: getInitials(pending.name) } : c
        ));
      }
      handleConversationSelect({ ...existing, name: pending.name || existing.name });
      pendingUserRef.current = null;
      return;
    }

    // No existing conversation — create it.
    pendingUserRef.current = null; // mark consumed to avoid re-entry
    (async () => {
      try {
        const token = await getFirebaseToken();
        if (!token) return;
        await apiClient.post(
          API_ENDPOINTS.createChat,
          { participants: [currentUid, pending.uid] },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        // The Firestore listener will pick up the new chat and re-render
        // the list; re-queue the pending intent so the next tick selects it.
        pendingUserRef.current = pending;
      } catch (err) {
        console.error('[SpConversations] Failed to create chat with', pending.uid, err);
        window.alert(`Couldn't open chat with ${pending.name || 'this user'}. Please try again.`);
      }
    })();
  }, [conversationList, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Click-outside for filter menu
  useEffect(() => {
    if (!showFilterMenu) return;
    const h = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setShowFilterMenu(false);
    };
    setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => document.removeEventListener('mousedown', h);
  }, [showFilterMenu]);

  // Cleanup messages listener
  useEffect(() => () => { if (messagesListenerRef.current) messagesListenerRef.current(); }, []);

  const getFirebaseToken = async () => {
    const u = auth.currentUser;
    if (!u) return null;
    return u.getIdToken();
  };

  // Fetch + cache user profile
  const fetchUserProfile = useCallback(async (uid) => {
    if (!uid) return { name: 'Unknown', email: '', profilePhotoUrl: null };
    if (userCacheRef.current[uid]) return userCacheRef.current[uid];
    if (pendingProfileReqs.current[uid]) {
      try { return await pendingProfileReqs.current[uid]; } catch { /* retry */ }
    }
    const req = (async () => {
      const token = await getFirebaseToken();
      if (!token) throw new Error('No token');
      const resp = await apiClient.get(API_ENDPOINTS.userByFirebaseUid(uid, true), {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      const d = resp.data;
      // Company-vendorType SPs have no personal firstName/lastName — only a
      // businessName — so a Company sender's messages would otherwise show
      // blank/email instead of their company name (see also the identical
      // fallback added to InviteInspectorPage.js's inviterName).
      const name = (d.vendorType === 'COMPANY' && d.businessName)
        ? d.businessName
        : ([d.firstName, d.lastName].filter(Boolean).join(' ') || d.name || d.email || 'Unknown');
      const profile = { name, email: d.email || '', profilePhotoUrl: d.profilePhotoBase64 || d.profilePhotoUrl || null };
      userCacheRef.current[uid] = profile;
      return profile;
    })();
    pendingProfileReqs.current[uid] = req;
    try { return await req; }
    catch { return { name: 'Unknown', email: '', profilePhotoUrl: null }; }
    finally { delete pendingProfileReqs.current[uid]; }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveChatDisplayInfo = useCallback(async (chatData, currentUid) => {
    const all = chatData.participants || [];
    const others = all.filter(u => u !== currentUid);
    if (others.length === 0) return { name: chatData.chatName || 'Chat', profilePhotoUrl: null };
    const isTxnChat = !!(chatData.dealId || chatData.transactionId);
    if (isTxnChat || others.length > 1) {
      return { name: chatData.chatName || `Group (${all.length})`, profilePhotoUrl: null };
    }
    if (chatData.chatName) return { name: chatData.chatName, profilePhotoUrl: null };
    // Prefer the participantsMap that the mobile SP stamps on create — it
    // avoids the /api/v1/users/{uid}/true lookup that returns "Unknown" in
    // environments where that endpoint is unreachable/permission-blocked for
    // SP→agent. Falls back to REST for chats created before the stamp existed.
    const mapEntry = chatData.participantsMap && chatData.participantsMap[others[0]];
    if (mapEntry?.name) {
      // Warm the shared user cache so any other consumer (e.g. header
      // rendering after selection) sees the same name without a REST call.
      userCacheRef.current[others[0]] = {
        name: mapEntry.name,
        email: '',
        profilePhotoUrl: mapEntry.profilePhotoUrl || null,
      };
      return { name: mapEntry.name, profilePhotoUrl: mapEntry.profilePhotoUrl || null };
    }
    const profile = await fetchUserProfile(others[0]);
    return { name: profile.name || 'Chat', profilePhotoUrl: profile.profilePhotoUrl || null };
  }, [fetchUserProfile]);

  // Firestore conversation list subscription.
  // Queries `chats` directly via participants array-contains — NOT
  // `user_chats/{uid}/chats` (what the agent app + /conversations use). The
  // service-provider mobile app's chat.service.ts (subscribeToConversations)
  // confirms user_chats is not populated for this role and queries `chats`
  // directly instead, with a per-chat sub-listener for unread counts; mirrored
  // here so SP conversations actually load.
  useEffect(() => {
    let unsubAuth = null;
    let unsubChats = null;
    const unreadListeners = unreadListenersRef.current;
    const unreadCounts = unreadCountsRef.current;

    const subscribeUnreadCount = (uid, chatId) => {
      if (unreadListeners[chatId]) return;
      const unsub = onSnapshot(
        query(collection(db, 'chats', chatId, 'messages'), where('senderId', '!=', uid)),
        (msgSnap) => {
          const count = msgSnap.docs.filter(d => !(d.data().readBy || []).includes(uid)).length;
          if (unreadCounts[chatId] === count) return;
          unreadCounts[chatId] = count;
          setConversationList(prev => prev.map(c => (c.id === chatId ? { ...c, unread: count } : c)));
        },
        () => {},
      );
      unreadListeners[chatId] = unsub;
    };

    unsubAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) { setLoading(false); return; }
      const uid = user.uid;
      setLoading(true);

      try {
        const q = query(
          collection(db, 'chats'),
          where('participants', 'array-contains', uid),
          limit(100),
        );

        unsubChats = onSnapshot(q, async (snapshot) => {
          try {
            const data = await Promise.all(snapshot.docs.map(async (chatSnap) => {
              const chatId = chatSnap.id;
              try {
                const chatData = chatSnap.data();
                // "Delete for me" — skip chats the current user has hidden.
                const hiddenBy = Array.isArray(chatData.hiddenBy) ? chatData.hiddenBy : [];
                if (hiddenBy.includes(uid)) return null;
                const displayInfo = await resolveChatDisplayInfo(chatData, uid);
                const txId = chatData.dealId || chatData.transactionId || null;
                const participants = chatData.participants || [];
                const isGroupChat = !!txId || participants.length > 2 || (!!chatData.chatName && participants.length > 1);
                subscribeUnreadCount(uid, chatId);
                return {
                  id: chatId,
                  name: displayInfo.name,
                  chatName: chatData.chatName || null,
                  initials: getInitials(displayInfo.name),
                  avatarColor: isGroupChat ? getGroupColor(displayInfo.name) : getAvatarColor(displayInfo.name),
                  profilePhotoUrl: isGroupChat ? null : displayInfo.profilePhotoUrl,
                  lastMessage: chatData.lastMessagePreview || '',
                  time: formatTimestamp(chatData.lastMessageAt),
                  lastMessageAt: chatData.lastMessageAt,
                  unread: unreadCounts[chatId] || 0,
                  participants,
                  transactionId: txId,
                  isGroupChat,
                };
              } catch { return null; }
            }));

            const valid = data
              .filter(Boolean)
              .filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i)
              .sort((a, b) => {
                const tA = a.lastMessageAt?.toDate ? a.lastMessageAt.toDate() : new Date(a.lastMessageAt || 0);
                const tB = b.lastMessageAt?.toDate ? b.lastMessageAt.toDate() : new Date(b.lastMessageAt || 0);
                return tB - tA;
              });

            // Drop unread sub-listeners for chats that dropped out of the list
            Object.keys(unreadListeners).forEach(chatId => {
              if (!valid.find(c => c.id === chatId)) {
                unreadListeners[chatId]();
                delete unreadListeners[chatId];
                delete unreadCounts[chatId];
              }
            });

            setConversationList(valid);
            setLoading(false);
          } catch { setLoading(false); }
        }, () => setLoading(false));
      } catch { setLoading(false); }
    });

    return () => {
      if (unsubAuth) unsubAuth();
      if (unsubChats) unsubChats();
      Object.values(unreadListeners).forEach(unsub => unsub());
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Messages subscription (Firestore + decrypted API content)
  const subscribeToMessages = useCallback((chatId) => {
    if (messagesListenerRef.current) { messagesListenerRef.current(); messagesListenerRef.current = null; }
    if (!chatId) return;

    decryptedCacheRef.current = new Map();
    setMessagesLoading(true);
    const cache = decryptedCacheRef.current;
    let fetchPromise = null;

    const fetchDecrypted = () => {
      if (fetchPromise) return fetchPromise;
      fetchPromise = (async () => {
        try {
          const token = await getFirebaseToken();
          if (!token) return;
          const resp = await apiClient.get(API_ENDPOINTS.chatMessages(chatId), {
            headers: { Authorization: `Bearer ${token}` }, params: { limit: 100 }, timeout: 10000,
          });
          const msgs = Array.isArray(resp.data) ? resp.data
            : Array.isArray(resp.data?.messages) ? resp.data.messages
            : Array.isArray(resp.data?.data) ? resp.data.data : [];
          msgs.forEach(m => {
            const id = m.messageId || m.id;
            if (id && m.content && !cache.has(id)) cache.set(id, m.content);
          });
        } catch { /* ignore */ } finally { fetchPromise = null; }
      })();
      return fetchPromise;
    };

    const currentUid = auth.currentUser?.uid;
    const msgsQ = query(collection(db, 'chats', chatId, 'messages'), orderBy('timestamp', 'asc'), limit(100));

    const unsub = onSnapshot(msgsQ, async (snapshot) => {
      const hasNew = snapshot.docs.some(d => !cache.has(d.id));
      if (hasNew) await fetchDecrypted();

      const mapped = snapshot.docs
        .filter(d => {
          const dt = d.data();
          return !dt.deletedForEveryone && !(currentUid && dt.deletedBy?.includes(currentUid));
        })
        .map(d => {
          const dt = d.data();
          const isSystem = (dt.messageType || '').toLowerCase() === 'system';
          const raw = dt.content !== '[Unable to decrypt message]' ? dt.content : '';
          const content = isSystem ? (raw || '') : (cache.get(d.id) || raw || '');
          const ts = dt.timestamp?.toDate?.() || (dt.timestamp ? new Date(dt.timestamp) : null);
          return {
            id: d.id,
            text: content,
            senderId: dt.senderId || '',
            senderName: dt.senderName || '',
            createdAt: ts?.toISOString() || '',
            isSent: dt.senderId === currentUid,
            time: formatTimestamp(ts),
            messageType: (dt.messageType || 'TEXT').toUpperCase(),
            attachments: dt.metadata?.attachments || dt.attachments || [],
          };
        });

      // Backend never stores senderName on the message doc (Message.java has
      // no such field) — every received message's senderName above is always
      // ''. Resolve it from each sender's profile before rendering, same as
      // /conversations' equivalent backfill — this is what actually makes a
      // name show up at all in a group chat like Vendor Broadcast (own
      // messages never show a name label, so only received ones need this).
      const uniqueSenderIds = [...new Set(
        mapped.filter(m => !m.isSent && m.senderId && !m.senderName).map(m => m.senderId),
      )];
      if (uniqueSenderIds.length > 0) {
        const profiles = await Promise.all(uniqueSenderIds.map(fetchUserProfile));
        const nameByUid = Object.fromEntries(uniqueSenderIds.map((uid, i) => [uid, profiles[i]?.name]));
        mapped.forEach(m => {
          if (!m.isSent && !m.senderName && nameByUid[m.senderId]) m.senderName = nameByUid[m.senderId];
        });
      }

      setMessages(mapped);
      setMessagesLoading(false);
      setTimeout(() => {
        if (messagesAreaRef.current) messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
      }, 50);
    }, () => setMessagesLoading(false));

    messagesListenerRef.current = unsub;
  }, [getFirebaseToken, fetchUserProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark a conversation's unread messages as read — a direct Firestore batch
  // write on each message's readBy/readAt (NOT the REST POST /chats/{id}/read
  // endpoint /conversations uses). Mirrors the service-provider mobile app's
  // chat.service.ts markMessagesAsRead exactly: this page's unread badges
  // (list dot + SpDashboardHeader nav badge) are both computed by reading
  // message.readBy directly, same as that app, so this is the write path that
  // actually clears them for the SP role.
  const markMessagesAsRead = useCallback(async (chatId) => {
    const currentUid = auth.currentUser?.uid;
    if (!chatId || !currentUid) return;
    try {
      const snapshot = await getDocs(
        query(collection(db, 'chats', chatId, 'messages'), where('senderId', '!=', currentUid)),
      );
      const unread = snapshot.docs.filter(d => !(d.data().readBy || []).includes(currentUid));
      if (unread.length === 0) return;
      const batch = writeBatch(db);
      unread.forEach(d => {
        batch.update(d.ref, {
          readBy: arrayUnion(currentUid),
          [`readAt.${currentUid}`]: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (error) {
      console.warn('[SpConversations] Failed to mark messages as read:', error?.message);
    }
  }, []);

  // "Delete for me" — add currentUid to chats/{id}.hiddenBy so it disappears
  // from this user's list only. Uses setDoc({merge:true}) with arrayUnion so
  // it's idempotent and doesn't overwrite other fields.
  const [deleteChatConfirm, setDeleteChatConfirm] = useState(null); // { id, name } | null
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const requestDeleteChat = (conv, e) => {
    if (e) e.stopPropagation();
    setDeleteChatConfirm({ id: conv.id, name: conv.name || 'this chat' });
  };
  const closeDeleteChatConfirm = () => { if (!isDeletingChat) setDeleteChatConfirm(null); };
  const confirmDeleteChat = async () => {
    if (!deleteChatConfirm) return;
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;
    const chatId = deleteChatConfirm.id;
    setIsDeletingChat(true);
    try {
      await setDoc(
        doc(db, 'chats', chatId),
        { hiddenBy: arrayUnion(currentUid) },
        { merge: true },
      );
      // Optimistic: also strip from local state so it disappears immediately
      // (the listener will re-emit without it once the write propagates).
      setConversationList(prev => prev.filter(c => c.id !== chatId));
      if (selectedId === chatId) {
        setSelectedId(null);
        setActiveConversation(null);
        setMessages([]);
      }
      setDeleteChatConfirm(null);
    } catch (err) {
      console.error('Failed to hide chat:', err);
      window.alert('Failed to delete chat. Please try again.');
    } finally {
      setIsDeletingChat(false);
    }
  };

  // Select a conversation
  const handleConversationSelect = async (conv) => {
    if (conv.id === RIA_ID) {
      setSelectedId(RIA_ID);
      setActiveConversation({ ...RIA_ENTRY, isFavorited: favorites.has(RIA_ID) });
      setMessages([]);
      setMessagesLoading(false);
      return;
    }
    setSelectedId(conv.id);
    setActiveConversation(conv);
    setMessages([]);
    setMessagesLoading(true);
    subscribeToMessages(conv.id);
    markMessagesAsRead(conv.id);
  };

  // ── New Chat panel — contacts picker + Add-by-email ──────────────────────
  // Loads the SP's contact graph (all roles). Called on panel open + after
  // Add-Contact success.
  // Plain function (not useCallback) — only called imperatively, never
  // passed as a prop or dep.
  const loadContacts = async () => {
    setContactsLoading(true);
    setContactsError(null);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const resp = await contactService.getContacts(
        token, 0, 100, 'SERVICE_PROVIDER',
      );
      const list = Array.isArray(resp?.content) ? resp.content : [];
      setContacts(list);
    } catch (err) {
      console.error('[SpConversations] Failed to load contacts:', err);
      setContactsError('Failed to load contacts. Please try again.');
    } finally {
      setContactsLoading(false);
    }
  };

  // ── "Broadcast to Vendors" (Company SP only) ─────────────────────────────────
  // Opens the panel — mirrors ConversationPage.js's openNewChat (Broker's
  // equivalent), minus the vendor-count fetch since the panel no longer
  // displays a count. Also warms the contact list so the picker below the
  // Broadcast section (and the whole panel for INDIVIDUAL SPs) renders
  // immediately when the user lands.
  const openNewChat = () => {
    setShowNewChat(true);
    setContactSearch('');
    loadContacts();
  };

  const closeNewChat = () => {
    setShowNewChat(false);
    setShowAddContact(false);
    setAddContactEmail('');
    setAddContactError(null);
  };

  // Start (or open) a 1:1 chat with a contact. Mirrors the deep-link
  // auto-create logic further up: check the live Firestore chat list for an
  // existing 2-participant conversation and open it; otherwise POST-create
  // and let the Firestore subscription surface the new chat, then select.
  const handleStartChatWithContact = async (contact) => {
    const contactUid = contact?.firebaseUid || contact?.firebase_uid || contact?.uid;
    if (!contactUid) {
      alert('This contact is missing a chat identity. Please try again later.');
      return;
    }
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;
    if (startingChatId) return;

    // Existing 1:1 chat — open directly, no POST.
    const existing = conversationList.find(c =>
      !c.isGroupChat
      && Array.isArray(c.participants)
      && c.participants.includes(contactUid)
      && c.participants.includes(currentUid)
      && c.participants.length === 2
    );
    if (existing) {
      closeNewChat();
      handleConversationSelect(existing);
      return;
    }

    setStartingChatId(contactUid);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const resp = await apiClient.post(
        API_ENDPOINTS.createChat,
        { participants: [currentUid, contactUid] },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const chatId = resp.data?.chatId || resp.data?.id;
      closeNewChat();
      if (chatId) {
        // The onSnapshot listener needs a tick to surface the new doc — poll
        // conversationList briefly, matches the pattern in composeBroadcast.
        setTimeout(() => {
          setConversationList(prev => {
            const found = prev.find(c => c.id === chatId);
            if (found) handleConversationSelect(found);
            return prev;
          });
        }, 1500);
      }
    } catch (err) {
      console.error('[SpConversations] Failed to create chat:', err);
      alert('Failed to start chat. Please try again.');
    } finally {
      setStartingChatId(null);
    }
  };

  // Add a Rialty user to the SP's contact graph by email. Server contract:
  // 201 → refresh + close; 404 → "email not on Rialty"; 409 → "already a
  // contact" (still useful — user probably just wants to start the chat).
  const handleAddContactByEmail = async () => {
    const trimmed = (addContactEmail || '').trim();
    if (!trimmed) {
      setAddContactError('Enter an email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setAddContactError('Enter a valid email address.');
      return;
    }
    setAddContactSubmitting(true);
    setAddContactError(null);
    try {
      const token = await getFirebaseToken();
      if (!token) return;
      await contactService.addContactByEmail(token, trimmed);
      setShowAddContact(false);
      setAddContactEmail('');
      loadContacts();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setAddContactError('No Rialty user found with that email. Ask them to sign up first.');
      } else if (status === 409) {
        setAddContactError('This user is already in your contacts.');
      } else {
        setAddContactError(err?.response?.data?.message || 'Failed to add contact. Please try again.');
      }
    } finally {
      setAddContactSubmitting(false);
    }
  };

  // Creates (or resolves the existing) "Vendor Broadcast" group chat with
  // every individual vendor linked to this company. Mirrors
  // ConversationPage.js's composeBroadcast/handleComposeBroadcast for Broker.
  //
  // POST /api/v1/chats always creates a brand-new chat document — there's no
  // server-side dedup — so clicking "Compose Broadcast" more than once used
  // to spawn a fresh "Vendor Broadcast" group every time. conversationList is
  // already a live Firestore subscription of every chat this user is in, so
  // check it first and just open the existing broadcast instead of creating
  // another one.
  const handleComposeBroadcast = async () => {
    const existing = conversationList.find(c => c.chatName === 'Vendor Broadcast');
    if (existing) {
      closeNewChat();
      handleConversationSelect(existing);
      return;
    }

    setIsBroadcasting(true);
    try {
      const token = await getFirebaseToken();
      const currentUid = auth.currentUser?.uid;
      if (!token || !currentUid) return;

      const idsResp = await apiClient.get(API_ENDPOINTS.spCompanyVendorIds, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = idsResp.data;
      const vendorIds = Array.isArray(raw) ? raw : (raw?.data || raw?.ids || []);

      if (vendorIds.length === 0) {
        alert('No vendors found in your company.');
        return;
      }

      const allParticipants = [currentUid, ...vendorIds.filter(id => id !== currentUid)];
      const resp = await apiClient.post(API_ENDPOINTS.createChat, {
        participants: allParticipants,
        chatName: 'Vendor Broadcast',
      }, { headers: { Authorization: `Bearer ${token}` } });

      const chatId = resp.data?.chatId || resp.data?.id;
      closeNewChat();
      if (chatId) {
        setTimeout(() => {
          setConversationList(prev => {
            const found = prev.find(c => c.id === chatId);
            if (found) handleConversationSelect(found);
            return prev;
          });
        }, 1500);
      }
    } catch (err) {
      console.error('Error creating Vendor Broadcast:', err);
      alert('Failed to create broadcast. Please try again.');
    } finally {
      setIsBroadcasting(false);
    }
  };

  // Send Ria message
  const handleSendRiaMessage = async () => {
    const text = messageInput.trim();
    if (!text) return;
    setMessageInput('');
    const userMsg = {
      id: `ria-user-${Date.now()}`, text, senderId: 'user', isSent: true,
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      createdAt: new Date().toISOString(), messageType: 'TEXT',
    };
    setRiaMessages(prev => [...prev, userMsg]);
    setTimeout(() => { if (messagesAreaRef.current) messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; }, 50);

    try {
      const token = await getFirebaseToken();
      if (!token) return;
      const body = { message: text };
      if (riaConvIdRef.current) body.conversation_id = riaConvIdRef.current;
      // timeout: Ria tool-call answers take ~40s against the local backend
      // (fast on GKE); the 30s apiClient default aborts before they finish.
      const resp = await apiClient.post(API_ENDPOINTS.riaChat, body, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
      if (resp.data?.conversation_id) riaConvIdRef.current = resp.data.conversation_id;
      const reply = resp.data?.response || resp.data?.reply || resp.data?.message;
      if (reply) {
        setRiaMessages(prev => [...prev, {
          id: `ria-reply-${Date.now()}`, text: reply, senderId: 'ria', senderName: 'Ria',
          isSent: false, isRia: true,
          time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
          createdAt: new Date().toISOString(), messageType: 'TEXT',
        }]);
        setTimeout(() => { if (messagesAreaRef.current) messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; }, 50);
      }
    } catch { /* silent */ }
  };

  // Send regular message
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedId) return;
    if (isRia) { handleSendRiaMessage(); return; }

    const text = messageInput.trim();
    setMessageInput('');
    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    const temp = {
      id: `temp-${Date.now()}`, text, senderId: currentUid, isSent: true,
      time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
      createdAt: new Date().toISOString(), messageType: 'TEXT', attachments: [],
    };
    setMessages(prev => [...prev, temp]);
    setTimeout(() => { if (messagesAreaRef.current) messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight; }, 50);

    try {
      const token = await getFirebaseToken();
      if (!token) throw new Error('Not authenticated');
      await apiClient.post(API_ENDPOINTS.chatMessages(selectedId),
        { content: text, messageType: 'text', attachments: [] },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
      );
    } catch {
      setMessages(prev => prev.filter(m => m.id !== temp.id));
      setMessageInput(text);
    }
  };

  const handleToggleFavorite = (id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Enrich conversations with job tags + favorites + Ria at top
  // Ria isn't part of the service-provider conversation experience (mirrors the
  // native SP app, which has no Ria/assistant chat screen) — omitted from the
  // list here, unlike the agent's /conversations page.
  const enrichedList = useMemo(() => conversationList.map(c => ({
    ...c,
    isFavorited: favorites.has(c.id),
    jobTag: c.isGroupChat ? 'Job' : undefined,
    jobAddress: c.isGroupChat ? (c.chatName || c.name) : undefined,
  })), [conversationList, favorites]);

  const filteredList = useMemo(() => enrichedList.filter(c => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return c.name.toLowerCase().includes(q) || (c.lastMessage || '').toLowerCase().includes(q);
    }
    if (activeFilter === 'unread')    return c.unread > 0;
    if (activeFilter === 'favorites') return c.isFavorited;
    return true;
  }), [enrichedList, searchQuery, activeFilter]);

  const displayedMessages = isRia ? riaMessages : messages;

  // Inject date separators into message list
  const messagesWithDates = useMemo(() => {
    const out = [];
    let lastDate = null;
    displayedMessages.forEach(msg => {
      const label = getDateLabel(msg.createdAt);
      if (label && label !== lastDate) { out.push({ type: 'date', label, id: `date-${label}` }); lastDate = label; }
      out.push({ type: 'message', ...msg });
    });
    return out;
  }, [displayedMessages]);

  // ── Render ──────────────────────────────────────────────────────────────────
  if (COMING_SOON) {
    const isSelectedChat = staticChatOpen && staticSelectedId === STATIC_CHAT_ID;

    return (
      <div className="spc-page">
        <div className="spc-outer">
          <div className="spc-card">

            {/* ── Left panel: conversation list — mirrors /conversations sidebar ── */}
            <div className="spd-left">
              <div className="spd-sidebar-header-row">
                <h1 className="spd-sidebar-title">Conversations</h1>
                <div className="spd-sidebar-header-actions">
                  <button type="button" className="spd-add-contact-btn" title="Add Contact">
                    <ContactPlusIcon />
                    <span>Add Contact</span>
                  </button>
                  <button type="button" className="spd-compose-btn" title="New Chat">
                    <EditIcon />
                  </button>
                </div>
              </div>

              <div className="spd-search-wrapper">
                <div className="spd-search-row">
                  <div className="spd-search-input-wrapper">
                    <span className="spd-search-icon"><SearchIcon /></span>
                    <input
                      className="spd-search-input"
                      type="text"
                      placeholder="Search"
                      value={staticSearch}
                      onChange={e => setStaticSearch(e.target.value)}
                    />
                  </div>
                  <div className="spd-filter-wrapper" ref={staticFilterRef}>
                    <button
                      type="button"
                      className={`spd-filter-btn${staticFilterOpen ? ' active' : ''}${staticFilter !== 'all' ? ' filtered' : ''}`}
                      title="Filter"
                      onClick={() => setStaticFilterOpen(v => !v)}
                    >
                      <FilterLinesIcon />
                      {staticFilter !== 'all' && <span className="spd-filter-dot" />}
                    </button>
                    {staticFilterOpen && (
                      <div className="spd-filter-dropdown">
                        {[
                          { key: 'all', label: 'All', icon: <FilterAllIcon /> },
                          { key: 'unread', label: 'Unread', icon: <FilterUnreadIcon /> },
                          { key: 'pinned', label: 'Pinned', icon: <FilterPinnedIcon /> },
                        ].map(({ key, label, icon }) => (
                          <button
                            key={key}
                            type="button"
                            className={`spd-filter-option${staticFilter === key ? ' active' : ''}`}
                            onClick={() => { setStaticFilter(key); setStaticFilterOpen(false); }}
                          >
                            <span className="spd-filter-check">{staticFilter === key && <CheckIcon />}</span>
                            <span className="spd-filter-option-icon">{icon}</span>
                            <span className="spd-filter-option-label">{label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="spd-conv-list">
                {staticFilteredRows.length === 0 ? (
                  <div className="spd-list-empty">No matching conversations</div>
                ) : staticFilteredRows.map(row => (
                  <div
                    key={row.id}
                    className={`spd-conv-item ${row.kind === 'ria' ? 'spd-ria-item' : ''} ${staticSelectedId === row.id ? 'active' : ''}`}
                    style={row.kind === 'ria' && HIDE_STATIC_RIA_ROW ? { display: 'none' } : undefined}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleStaticRowClick(row)}
                    onKeyDown={e => e.key === 'Enter' && handleStaticRowClick(row)}
                  >
                    <div className="spd-conv-avatar">
                      {row.kind === 'ria' ? (
                        <img src={riaLogo} alt="Ria" className="spd-avatar-circle spd-ria-avatar" />
                      ) : row.kind === 'group' ? (
                        <div className="spd-avatar-circle" style={{ background: row.groupColor }}><GroupIcon /></div>
                      ) : (
                        <div className="spd-avatar-circle" style={{ background: row.avatarColor }}>{row.initials}</div>
                      )}
                    </div>

                    <div className="spd-conv-info">
                      <div className="spd-conv-header-row">
                        <h3 className="spd-conv-name">{row.name}</h3>
                        <span className="spd-conv-time">{row.time}</span>
                      </div>
                      <div className="spd-conv-bottom-row">
                        <p className="spd-conv-preview">{row.preview}</p>
                        <div className="spd-conv-actions">
                          {row.unread > 0 && <span className="spd-unread-badge">{row.unread}</span>}
                          {row.kind === 'ria' ? (
                            <span className="spd-ria-star"><StarFilledIcon /></span>
                          ) : row.kind === 'dm' && (
                            <span className={`spd-star-icon ${row.starred ? 'starred' : ''}`}>
                              {row.starred ? <StarFilledIcon /> : <StarEmptyIcon />}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right panel: active conversation — mirrors /conversations main ── */}
            <div className="spd-right">
              {!isSelectedChat ? (
                <div className="spd-empty-state">
                  <p>Select a conversation to start messaging</p>
                </div>
              ) : (
                <>
                  {/* Header — avatar + name only, no call/dots/plus buttons */}
                  <div className="spd-chat-header">
                    <div className="spd-header-center">
                      <div className="spd-avatar-circle spd-header-avatar" style={{ background: STATIC_CHAT.groupColor }}>
                        <GroupIcon />
                      </div>
                      <div className="spd-header-name-group">
                        <h2 className="spd-header-name">{STATIC_CHAT.name}</h2>
                        {STATIC_CHAT.isGroup && (
                          <p className="spd-header-participant-count">{STATIC_CHAT.participantCount} participants</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="spd-messages-area">
                    {STATIC_CHAT.groups.map(group => (
                      <React.Fragment key={group.date}>
                        <div className="spd-message-date-separator"><span>{group.date}</span></div>
                        {group.messages.map((m, i) => (
                          <div key={i} className={`spd-message ${m.from === 'me' ? 'spd-message-sent' : 'spd-message-received'}`}>
                            {m.from !== 'me' && (
                              <div className="spd-message-avatar">
                                <div className="spd-avatar-circle-small" style={{ background: getAvatarColor(m.name) }}>
                                  {getInitials(m.name)}
                                </div>
                              </div>
                            )}
                            <div className="spd-message-content">
                              {m.from !== 'me' && (
                                <div className="spd-message-sender">{m.name}, {m.time}</div>
                              )}
                              <div className="spd-message-bubble">{m.text}</div>
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className="spd-message-input-container">
                    <AskRiaInputBar
                      value={staticMessageInput}
                      onChange={setStaticMessageInput}
                      onSend={() => setStaticMessageInput('')}
                      placeholder={`Message ${STATIC_CHAT.name}...`}
                      onAddClick={() => {}}
                      addButtonTitle="Attach file"
                    />
                  </div>
                </>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="spc-page">
      <div className="spc-outer">
        <div className="spc-card">

          {/* ── Left panel: conversation list ── */}
          <aside className="spc-left">
            <div className="spc-conv-title-row">
              <h1 className="spc-conv-title">Conversations</h1>
              {!showNewChat && (
                <button
                  type="button"
                  className="spc-compose-btn"
                  title={spVendorType === 'COMPANY' ? 'New chat / Broadcast' : 'New chat'}
                  onClick={openNewChat}
                >
                  <EditIcon />
                </button>
              )}
            </div>

            {showNewChat ? (
              /* ── New Chat panel — Broadcast (Company only) + contact picker ── */
              <div className="spc-newchat-panel">
                <div className="spc-newchat-header">
                  <button className="spc-newchat-back-btn" onClick={closeNewChat} title="Back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"/>
                    </svg>
                  </button>
                  <h2 className="spc-newchat-title">New Chat</h2>
                  <button
                    type="button"
                    className="spc-newchat-add-btn"
                    onClick={() => { setAddContactError(null); setAddContactEmail(''); setShowAddContact(true); }}
                    title="Add contact by email"
                  >
                    <ContactPlusIcon />
                    <span>Add Contact</span>
                  </button>
                </div>

                {/* Broadcast is Company-only; INDIVIDUAL SPs skip this
                    section and see the contact picker directly below. */}
                {spVendorType === 'COMPANY' && (
                  <div className="spc-broadcast-section">
                    <div className="spc-broadcast-info-row">
                      <div className="spc-broadcast-icon-wrap">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="0">
                          <path d="M18 11v-1a2 2 0 0 0-2-2H4l-2 6h16a2 2 0 0 0 2-2v-1zM4 11h10M3 15l1-4"/>
                          <path d="M22 5L2 12.5" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="19" cy="5" r="3" fill="white"/>
                        </svg>
                      </div>
                      <div className="spc-broadcast-text">
                        <div className="spc-broadcast-title">Broadcast to Vendors</div>
                        <div className="spc-broadcast-subtitle">
                          Send a message to all inspectors in your company
                        </div>
                      </div>
                    </div>
                    <button className="spc-broadcast-btn" onClick={handleComposeBroadcast} disabled={isBroadcasting}>
                      {isBroadcasting ? 'Creating...' : 'Compose Broadcast'}
                    </button>
                  </div>
                )}

                {/* Contact picker — 1:1 chat with any AGENT or fellow SP the
                    caller already has a platform relationship with. Add
                    Contact by Email (header button above) is the escape
                    hatch when the target isn't in the list yet. */}
                <div className="spc-newchat-picker-header">
                  {spVendorType === 'COMPANY' ? 'Or start a 1:1 chat' : 'Start a chat'}
                </div>
                <div className="spd-search-wrapper">
                  <div className="spd-search-row">
                    <div className="spd-search-input-wrapper">
                      <span className="spd-search-icon"><SearchIcon /></span>
                      <input
                        className="spd-search-input"
                        type="text"
                        placeholder="Search contacts..."
                        value={contactSearch}
                        onChange={e => setContactSearch(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="spc-newchat-contact-list">
                  {contactsLoading ? (
                    <div className="spc-list-empty">Loading contacts…</div>
                  ) : contactsError ? (
                    <div className="spc-list-empty" style={{ color: '#C0392B' }}>
                      {contactsError}
                    </div>
                  ) : (() => {
                    const q = contactSearch.trim().toLowerCase();
                    const filtered = q.length === 0 ? contacts : contacts.filter(c => {
                      const full = `${c.firstName || ''} ${c.lastName || ''}`.trim().toLowerCase();
                      return full.includes(q) || (c.email || '').toLowerCase().includes(q);
                    });
                    if (filtered.length === 0) {
                      return (
                        <div className="spc-list-empty">
                          {q ? 'No matching contacts.'
                             : 'No contacts yet. Add one by email to get started.'}
                        </div>
                      );
                    }
                    return filtered.map(contact => {
                      const displayName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.email || 'Contact';
                      const initials = getInitials(displayName);
                      const isStarting = startingChatId === (contact.firebaseUid || contact.uid);
                      const roleLabel = Array.isArray(contact.roles) && contact.roles.length > 0
                        ? contact.roles[0].charAt(0) + contact.roles[0].slice(1).toLowerCase()
                        : null;
                      return (
                        <div
                          key={contact.id || contact.firebaseUid || contact.email}
                          className="spc-newchat-contact-item"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleStartChatWithContact(contact)}
                          onKeyDown={e => e.key === 'Enter' && handleStartChatWithContact(contact)}
                        >
                          {contact.profilePhotoUrl ? (
                            <img className="spc-avatar spc-avatar-img" src={contact.profilePhotoUrl} alt="" />
                          ) : (
                            <div className="spc-avatar" style={{ background: '#3D2FA0' }}>
                              {initials}
                            </div>
                          )}
                          <div className="spc-newchat-contact-info">
                            <div className="spc-newchat-contact-name">{displayName}</div>
                            <div className="spc-newchat-contact-sub">
                              {roleLabel ? `${roleLabel} · ` : ''}{contact.email || ''}
                            </div>
                          </div>
                          {isStarting && (
                            <div className="spc-newchat-contact-spinner">…</div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            ) : (
            <>
            {/* Search + filter bar — mirrors /conversations (search input + icon
                filter button opening an icon dropdown), not a plain text dropdown */}
            <div className="spd-search-wrapper">
              <div className="spd-search-row">
                <div className="spd-search-input-wrapper">
                  <span className="spd-search-icon"><SearchIcon /></span>
                  <input
                    className="spd-search-input"
                    type="text"
                    placeholder="Search conversations..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="spd-filter-wrapper" ref={filterMenuRef}>
                  <button
                    type="button"
                    className={`spd-filter-btn${showFilterMenu ? ' active' : ''}${activeFilter !== 'all' ? ' filtered' : ''}`}
                    title="Filter"
                    onClick={() => setShowFilterMenu(v => !v)}
                  >
                    <FilterLinesIcon />
                    {activeFilter !== 'all' && <span className="spd-filter-dot" />}
                  </button>
                  {showFilterMenu && (
                    <div className="spd-filter-dropdown">
                      {[
                        { key: 'all', label: 'All', icon: <FilterAllIcon /> },
                        { key: 'unread', label: 'Unread', icon: <FilterUnreadIcon /> },
                        { key: 'favorites', label: 'Favorites', icon: <FilterPinnedIcon /> },
                      ].map(({ key, label, icon }) => (
                        <button
                          key={key}
                          type="button"
                          className={`spd-filter-option${activeFilter === key ? ' active' : ''}`}
                          onClick={() => { setActiveFilter(key); setShowFilterMenu(false); }}
                        >
                          <span className="spd-filter-check">{activeFilter === key && <CheckIcon />}</span>
                          <span className="spd-filter-option-icon">{icon}</span>
                          <span className="spd-filter-option-label">{label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* List */}
            <div className="spc-conv-list">
              {loading ? (
                <div className="spc-list-empty">Loading…</div>
              ) : filteredList.length === 0 ? (
                <div className="spc-list-empty">
                  {searchQuery ? 'No matching conversations.' : activeFilter === 'unread' ? 'No unread conversations.' : activeFilter === 'favorites' ? 'No favourites yet.' : 'No conversations yet.'}
                </div>
              ) : filteredList.map((conv) => {
                const isSelected = conv.id === selectedId;

                return (
                  <div
                    key={conv.id}
                    className={`spc-conv-item ${isSelected ? 'selected' : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleConversationSelect(conv)}
                    onKeyDown={e => e.key === 'Enter' && handleConversationSelect(conv)}
                  >
                    {/* Avatar */}
                    {conv.profilePhotoUrl ? (
                      <img className="spc-avatar spc-avatar-img" src={conv.profilePhotoUrl} alt="" />
                    ) : conv.id === RIA_ID ? (
                      <div className="spc-avatar spc-avatar-ria"><RiaBotIcon /></div>
                    ) : (
                      <div className="spc-avatar" style={{ background: conv.avatarColor }}>
                        {conv.initials}
                      </div>
                    )}

                    {/* Body */}
                    <div className="spc-conv-body">
                      <div className="spc-conv-top">
                        <p className={`spc-conv-name ${conv.unread > 0 ? 'unread' : ''}`}>{conv.name}</p>
                        <span className="spc-conv-time">{conv.time}</span>
                      </div>

                      {conv.jobTag && (
                        <span className="spc-job-tag">{conv.jobTag}</span>
                      )}

                      <div className="spc-conv-bottom">
                        <p className={`spc-conv-preview ${conv.unread > 0 ? 'unread' : ''}`}>
                          {conv.lastMessage}
                        </p>
                        <div className="spc-conv-actions">
                          {conv.id !== RIA_ID && (
                            <button
                              type="button"
                              className="spc-conv-delete"
                              title="Delete chat"
                              aria-label="Delete chat"
                              onClick={e => requestDeleteChat(conv, e)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          )}
                          <span
                            className={`spc-star ${conv.isFavorited ? 'favorited' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={conv.isFavorited ? 'Unfavorite' : 'Favorite'}
                            onClick={e => { e.stopPropagation(); handleToggleFavorite(conv.id); }}
                            onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); handleToggleFavorite(conv.id); } }}
                          >
                            {conv.unread > 0
                              ? <span className="spc-unread-dot" />
                              : conv.isFavorited ? <StarFilledIcon /> : <StarEmptyIcon />
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
            )}
          </aside>

          {/* ── Right panel: message area ── */}
          <section className="spc-right">
            {!selectedId ? (
              <div className="spc-empty-state">
                <div className="spc-empty-icon"><UsersIcon /></div>
                <p className="spc-empty-title">Select a conversation</p>
                <p className="spc-empty-sub">Choose a conversation from the list to start messaging.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="spc-msg-header">
                  {activeConversation?.profilePhotoUrl ? (
                    <img className="spc-hdr-avatar spc-avatar-img" src={activeConversation.profilePhotoUrl} alt="" />
                  ) : selectedId === RIA_ID ? (
                    <div className="spc-hdr-avatar spc-avatar-ria"><RiaBotIcon /></div>
                  ) : (
                    <div className="spc-hdr-avatar" style={{ background: activeConversation?.avatarColor }}>
                      {activeConversation?.initials}
                    </div>
                  )}
                  <div className="spc-hdr-info">
                    <p className="spc-hdr-name">{activeConversation?.name}</p>
                    {activeConversation?.isGroupChat && (
                      <p className="spc-hdr-sub">{(activeConversation.participants?.length || 0)} participants</p>
                    )}
                  </div>
                </div>

                {/* Job context banner — group/transaction chats */}
                {activeConversation?.jobAddress && (
                  <div className="spc-job-banner">
                    <span className="spc-jb-icon"><ClipboardIcon /></span>
                    <div className="spc-jb-text">
                      <p className="spc-jb-addr">{activeConversation.jobAddress}</p>
                      {activeConversation.jobType && (
                        <p className="spc-jb-meta">{activeConversation.jobType}{activeConversation.jobDateTime ? ` · ${activeConversation.jobDateTime}` : ''}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Messages */}
                <div className="spc-messages" ref={messagesAreaRef}>
                  {messagesLoading ? (
                    <div className="spc-msgs-loading">Loading messages…</div>
                  ) : messagesWithDates.length === 0 ? (
                    <div className="spc-msgs-empty">No messages yet. Say hello!</div>
                  ) : messagesWithDates.map(item =>
                    item.type === 'date' ? (
                      <div key={item.id} className="spc-date-sep">
                        <span>{item.label}</span>
                      </div>
                    ) : (
                      <div key={item.id} className={`spc-msg-row ${item.isSent ? 'sent' : 'received'}`}>
                        {!item.isSent && (
                          <div className="spc-msg-avatar" style={{ background: item.isRia ? '#3d2d8f' : getAvatarColor(item.senderName || item.senderId) }}>
                            {item.isRia ? <RiaBotIcon /> : getInitials(item.senderName || 'U')}
                          </div>
                        )}
                        <div className="spc-msg-body">
                          {!item.isSent && item.senderName && (
                            <p className="spc-msg-sender">{item.senderName}</p>
                          )}
                          <div className={`spc-bubble ${item.isSent ? 'sent' : 'received'} ${item.messageType === 'SYSTEM' ? 'system' : ''}`}>
                            {item.messageType === 'SYSTEM' ? (
                              <em>{item.text}</em>
                            ) : (
                              item.text || (item.attachments?.length > 0 ? `📎 ${item.attachments[0].fileName || 'Attachment'}` : '')
                            )}
                          </div>
                          <span className="spc-msg-time">{item.time}</span>
                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* Composer — same AskRiaInputBar component /conversations uses */}
                <div className="spc-composer">
                  <AskRiaInputBar
                    value={messageInput}
                    onChange={setMessageInput}
                    onSend={handleSendMessage}
                    placeholder={isRia ? 'Ask Ria anything...' : `Message ${activeConversation?.name || 'conversation'}...`}
                    onAddClick={() => {}}
                    addButtonTitle="Attach file"
                  />
                </div>
              </>
            )}
          </section>

        </div>
      </div>

      {/* Add-contact-by-email modal — inline overlay reusing the delete-modal
          skin so both flows look/feel identical. Server contract: 404 = "not
          on Rialty" (surface as a hint to invite them), 409 = "already a
          contact" (rare but a valid outcome). */}
      {showAddContact && (
        <div
          className="spc-delete-modal-overlay"
          onClick={() => { if (!addContactSubmitting) setShowAddContact(false); }}
        >
          <div className="spc-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="spc-delete-modal-title">Add Contact</div>
            <div className="spc-delete-modal-desc" style={{ marginBottom: 12 }}>
              Enter the email of a Rialty user to add them to your contacts.
              You'll then be able to start a chat with them.
            </div>
            <input
              className="spd-search-input"
              type="email"
              placeholder="name@example.com"
              value={addContactEmail}
              onChange={e => { setAddContactEmail(e.target.value); setAddContactError(null); }}
              onKeyDown={e => { if (e.key === 'Enter' && !addContactSubmitting) handleAddContactByEmail(); }}
              disabled={addContactSubmitting}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #DDD', borderRadius: 8, fontSize: 14 }}
              autoFocus
            />
            {addContactError && (
              <div style={{ color: '#C0392B', fontSize: 13, marginTop: 8 }}>
                {addContactError}
              </div>
            )}
            <div className="spc-add-contact-actions">
              <button
                className="spc-delete-modal-btn spc-add-contact-btn-primary"
                onClick={handleAddContactByEmail}
                disabled={addContactSubmitting}
              >
                {addContactSubmitting ? 'Adding…' : 'Add Contact'}
              </button>
              <button
                className="spc-delete-modal-btn spc-delete-modal-no"
                onClick={() => setShowAddContact(false)}
                disabled={addContactSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete chat confirmation — mirrors the jp-delete-modal design language
          on JobDetailPage so both flows look/feel identical. */}
      {deleteChatConfirm && (
        <div className="spc-delete-modal-overlay" onClick={closeDeleteChatConfirm}>
          <div className="spc-delete-modal" onClick={e => e.stopPropagation()}>
            <div className="spc-delete-modal-title">Delete chat</div>
            <div className="spc-delete-modal-desc">
              Delete your copy of the chat with <strong>{deleteChatConfirm.name}</strong>?
              The other person will still see the messages.
            </div>
            <div className="spc-delete-modal-actions">
              <button
                className="spc-delete-modal-btn spc-delete-modal-yes"
                onClick={confirmDeleteChat}
                disabled={isDeletingChat}
              >
                {isDeletingChat ? 'Deleting…' : 'Delete'}
              </button>
              <button
                className="spc-delete-modal-btn spc-delete-modal-no"
                onClick={closeDeleteChatConfirm}
                disabled={isDeletingChat}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpConversationsPage;
